import axios from 'axios';
import { URLS } from 'routes/Urls';

const normalizeTaskCustomerPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return payload;
  const infoTaskCustomer = payload?.info?.general?.task_customer;
  if (infoTaskCustomer === undefined) return payload;
  return {
    ...payload,
    task_customer: infoTaskCustomer,
  };
};

const createProgressSimulator = (progressCallback, duration = 10000) => {
  let startTime = Date.now();
  let intervalId;
  let isCompleted = false;

  const simulate = () => {
    if (isCompleted) return;

    const elapsed = Date.now() - startTime;
    const progress = Math.min(Math.round((elapsed / duration) * 90), 90);

    progressCallback(progress);

    if (progress >= 90) {
      clearInterval(intervalId);
    }
  };

  intervalId = setInterval(simulate, 200);

  return {
    complete: () => {
      isCompleted = true;
      clearInterval(intervalId);
      progressCallback(100);
    },
    stop: () => {
      isCompleted = true;
      clearInterval(intervalId);
    }
  };
};

export const sendHAReportWithProgress = async (formData, progressCallback) => {
  let progressEventCount = 0;
  let progressSimulator = null;
  let simulatorTimeout = null;

  try {
    simulatorTimeout = setTimeout(() => {
      if (progressEventCount === 0) {
        console.warn('🚫 NGINX BUFFERING DETECTED: Starting fallback progress simulation for HA report');
        progressSimulator = createProgressSimulator(progressCallback, 8000);
      }
    }, 500);

    const payload = normalizeTaskCustomerPayload(formData);
    const response = await axios.put(`${URLS.CREATE_REPORT_HA}${formData.id}/`, payload, {
      onUploadProgress: (progressEvent) => {
        progressEventCount++;

        if (progressSimulator) {
          progressSimulator.stop();
          progressSimulator = null;
        }

        if (progressEvent.lengthComputable && progressEvent.total > 0) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          progressCallback(percentCompleted);
        } else {
          progressCallback(50);
        }
      },
      timeout: 30000,
    });

    clearTimeout(simulatorTimeout);

    if (progressSimulator) {
      progressSimulator.complete();
    } else {
      progressCallback(100);
    }

    if (progressEventCount === 0) {
      console.warn('🚫 NGINX BUFFERING CONFIRMED: No upload progress events received');
    }

    return response;
  } catch (error) {
    clearTimeout(simulatorTimeout);
    if (progressSimulator) {
      progressSimulator.stop();
    }
    throw error;
  }
};

export const sendPhotosWithProgress = async (reportId, photos, progressCallback) => {
  const files = photos.map((photo) => photo.file);
  const formData = new FormData();

  files.forEach((file) => {
    if (file.file) {
      formData.append('images', file.file);
    } else {
      formData.append('images', file);
    }
  });

  let progressEventCount = 0;
  let progressSimulator = null;
  let simulatorTimeout = null;

  try {
    simulatorTimeout = setTimeout(() => {
      if (progressEventCount === 0) {
        console.warn('🚫 NGINX BUFFERING DETECTED: Starting fallback progress simulation for photos');
        progressSimulator = createProgressSimulator(progressCallback, 12000);
      }
    }, 500);

    const response = await axios.patch(`${URLS.CREATE_REPORT_HA}${reportId}/images/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        progressEventCount++;

        if (progressSimulator) {
          progressSimulator.stop();
          progressSimulator = null;
        }

        if (progressEvent.lengthComputable && progressEvent.total > 0) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          progressCallback(percentCompleted);
        } else {
          progressCallback(50);
        }
      },
      timeout: 60000,
    });

    clearTimeout(simulatorTimeout);

    if (progressSimulator) {
      progressSimulator.complete();
    } else {
      progressCallback(100);
    }

    if (progressEventCount === 0) {
      console.warn('🚫 NGINX BUFFERING CONFIRMED: No upload progress events for photos');
    }

    return response;
  } catch (error) {
    clearTimeout(simulatorTimeout);
    if (progressSimulator) {
      progressSimulator.stop();
    }
    throw error;
  }
};

export const sendAnnexPhotosWithProgress = async (reportId, annexImages, progressCallback) => {
  const formData = new FormData();

  annexImages.forEach((file) => {
    if (file.file) {
      formData.append('images', file.file);
    } else {
      formData.append('images', file);
    }
  });

  let progressEventCount = 0;
  let progressSimulator = null;
  let simulatorTimeout = null;

  try {
    simulatorTimeout = setTimeout(() => {
      if (progressEventCount === 0) {
        console.warn('🚫 NGINX BUFFERING DETECTED: Starting fallback progress simulation for annex maps');
        progressSimulator = createProgressSimulator(progressCallback, 10000);
      }
    }, 500);

    const response = await axios.patch(`${URLS.CREATE_REPORT_HA}${reportId}/images/millimeter-map/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        progressEventCount++;

        if (progressSimulator) {
          progressSimulator.stop();
          progressSimulator = null;
        }

        if (progressEvent.lengthComputable && progressEvent.total > 0) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          progressCallback(percentCompleted);
        } else {
          progressCallback(50);
        }
      },
      timeout: 60000,
    });

    clearTimeout(simulatorTimeout);

    if (progressSimulator) {
      progressSimulator.complete();
    } else {
      progressCallback(100);
    }

    if (progressEventCount === 0) {
      console.warn('🚫 NGINX BUFFERING CONFIRMED: No upload progress events for annex maps');
    }

    return response;
  } catch (error) {
    clearTimeout(simulatorTimeout);
    if (progressSimulator) {
      progressSimulator.stop();
    }
    throw error;
  }
};

export const sendEOREReportWithProgress = async (formData, reportId, progressCallback) => {
  let progressEventCount = 0;
  let progressSimulator = null;
  let simulatorTimeout = null;

  try {
    simulatorTimeout = setTimeout(() => {
      if (progressEventCount === 0) {
        console.warn('🚫 NGINX BUFFERING DETECTED: Starting fallback progress simulation for EORE report');
        progressSimulator = createProgressSimulator(progressCallback, 8000);
      }
    }, 500);

    const payload = normalizeTaskCustomerPayload(formData);
    const response = await axios.patch(`/reports/eore/${reportId}/`, payload, {
      onUploadProgress: (progressEvent) => {
        progressEventCount++;

        if (progressSimulator) {
          progressSimulator.stop();
          progressSimulator = null;
        }

        if (progressEvent.lengthComputable && progressEvent.total > 0) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          progressCallback(percentCompleted);
        } else {
          progressCallback(50);
        }
      },
      timeout: 30000,
    });

    clearTimeout(simulatorTimeout);

    if (progressSimulator) {
      progressSimulator.complete();
    } else {
      progressCallback(100);
    }

    if (progressEventCount === 0) {
      console.warn('🚫 NGINX BUFFERING CONFIRMED: No upload progress events for EORE report');
    }

    return response;
  } catch (error) {
    clearTimeout(simulatorTimeout);
    if (progressSimulator) {
      progressSimulator.stop();
    }
    throw error;
  }
};

export const sendEOREFilesWithProgress = async (files, reportId, type, progressCallback) => {
  const formData = new FormData();
  files?.forEach((file) => {
    formData.append(type, file.file || file);
  });

  let progressEventCount = 0;
  let progressSimulator = null;
  let simulatorTimeout = null;

  try {
    simulatorTimeout = setTimeout(() => {
      if (progressEventCount === 0) {
        console.warn(`🚫 NGINX BUFFERING DETECTED: Starting fallback progress simulation for EORE ${type}`);
        progressSimulator = createProgressSimulator(progressCallback, 10000);
      }
    }, 500);

    const url = `/reports/eore/${reportId}/${type === 'images' ? 'images' : 'files'}/`;
    const response = await axios.patch(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        progressEventCount++;

        if (progressSimulator) {
          progressSimulator.stop();
          progressSimulator = null;
        }

        if (progressEvent.lengthComputable && progressEvent.total > 0) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          progressCallback(percentCompleted);
        } else {
          progressCallback(50);
        }
      },
      timeout: 60000,
    });

    clearTimeout(simulatorTimeout);

    if (progressSimulator) {
      progressSimulator.complete();
    } else {
      progressCallback(100);
    }

    if (progressEventCount === 0) {
      console.warn(`🚫 NGINX BUFFERING CONFIRMED: No upload progress events for EORE ${type}`);
    }

    return response;
  } catch (error) {
    clearTimeout(simulatorTimeout);
    if (progressSimulator) {
      progressSimulator.stop();
    }
    throw error;
  }
};

export const createEOREReportWithProgress = async (formData, progressCallback) => {
  let progressEventCount = 0;
  let progressSimulator = null;
  let simulatorTimeout = null;

  try {
    simulatorTimeout = setTimeout(() => {
      if (progressEventCount === 0) {
        console.warn('🚫 NGINX BUFFERING DETECTED: Starting fallback progress simulation for EORE creation');
        progressSimulator = createProgressSimulator(progressCallback, 8000);
      }
    }, 500);

    const payload = normalizeTaskCustomerPayload(formData);
    const response = await axios.post('/reports/eore/', payload, {
      onUploadProgress: (progressEvent) => {
        progressEventCount++;

        if (progressSimulator) {
          progressSimulator.stop();
          progressSimulator = null;
        }

        if (progressEvent.lengthComputable && progressEvent.total > 0) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          progressCallback(percentCompleted);
        } else {
          progressCallback(50);
        }
      },
      timeout: 30000,
    });

    clearTimeout(simulatorTimeout);

    if (progressSimulator) {
      progressSimulator.complete();
    } else {
      progressCallback(100);
    }

    if (progressEventCount === 0) {
      console.warn('🚫 NGINX BUFFERING CONFIRMED: No upload progress events for EORE creation');
    }

    return response;
  } catch (error) {
    clearTimeout(simulatorTimeout);
    if (progressSimulator) {
      progressSimulator.stop();
    }
    throw error;
  }
};